import { EndpointV2, Provider } from "@aws-sdk/types";

export type Credentials = {
  id: string;
  access_key: string;
  access_secret: string;
  endpoint: string;
  owner: string;
};

export type TableCredentials = {
  id: string;
  accessKey: string;
  accessSecret: string;
  endpoint: string;
  owner: string;
};

export type GetCredentialsParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
};

export type GetCredentialsResponse = Credentials;

export type GetCredentialsListParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
};

export type GetCredentialsListResponse = Credentials[];

export type CreateCredentialsParams = {
  regionId: string;
  projId: string;
};

export type CreateCredentialsResponse = Credentials;

export type RegenerateCredentialsParams = {
  regionId: string;
  projId: string;
  credsId: string;
};

export type RegenerateCredentialsResponse = Credentials;

export type DeleteCredentialsParams = {
  regionId: string;
  projId: string;
  credsId: string;
};

/*
  Subusers.

  A subuser is identified by its name, and a key by its access key: neither the
  list response nor a single record carries a dedicated id, so the table
  selectors derive `id` from those fields.
*/
export type SubUserKey = {
  access_key: string;
  secret_key: string;
};

export type SubUser = {
  name: string;
  full_name: string;
  principal_arn: string;
  access: string; // "none" | "read" | "write" | "readwrite" | "full"
  keys: SubUserKey[];
};

export type TableSubUserKey = {
  id: string;
  accessKey: string;
  secretKey: string;
};

export type TableSubUser = {
  id: string;
  name: string;
  fullName: string;
  principalArn: string;
  access: string;
  keysCount: string;
  keys: TableSubUserKey[];
};

export type GetSubUsersParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
};
export type GetSubUsersResponse = SubUser[];

export type CreateSubUserParams = {
  regionId: string;
  projId: string;
  credsId: string;
  data: {
    name: string;
    access: string;
  };
};
export type CreateSubUserResponse = SubUser;

export type DeleteSubUserParams = {
  regionId: string;
  projId: string;
  credsId: string;
  subUserName: string;
};

export type GetSubUserKeyParams = {
  regionId: string;
  projId: string;
  credsId: string;
  subUserName: string;
  accessKey: string;
};
/*
  The sibling key endpoints answer with the subuser's whole key list, so a
  single key may arrive either on its own or wrapped in an array.
*/
export type GetSubUserKeyResponse = SubUserKey | SubUserKey[];

export type CreateSubUserKeyParams = {
  regionId: string;
  projId: string;
  credsId: string;
  subUserName: string;
};
export type CreateSubUserKeyResponse = SubUserKey[];

export type DeleteSubUserKeyParams = {
  regionId: string;
  projId: string;
  credsId: string;
  subUserName: string;
  accessKey: string;
};

export type TableS3Buckets = {
  id: string;
  name: string;
  visibility: string;
  url: string;
  creationDate: string;
  is_versioning: string;
};

export type S3Bucket = {
  name: string;
  visibility: string;
  url: string;
  creation_date: string;
  is_versioning: string;
};

export type GetBucketParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
};
export type GetBucketResponse = S3Bucket;

export type GetBucketsParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
};
export type GetBucketsResponse = S3Bucket[];

export type CreateBucketParams = {
  regionId: string;
  projId: string;
  credsId: string;
  data: {
    name: string;
  };
};
export type CreateBucketResponse = S3Bucket;

export type DeleteBucketParams = {
  regionId: string;
  projId: string;
  credsId: string;
  name: string;
};

export type S3Files = {
  name: string;
  key: string;
  last_modified: string;
  size: string;
  is_folder: boolean;
  visibility: string;
  url: string;
};

export type S3FilesTotal = {
  objects: S3Files[];
  key_count: number;
  total_keys: number;
  is_flat_list: boolean;
  page_map: { [key: number]: string } | Record<string, never>;
  is_next_page_available: boolean;
};

export type TableS3Files = {
  id: string;
  key: string;
  name: string;
  lastModified: string;
  isFolder: boolean;
  size: string;
  visibility: string;
  url: string;
};

export type GetFilesParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  folderName?: string;
  offset?: number;
  limit?: number;
};
export type GetFilesResponse = {
  bucket_name: string;
  key_count: number;
  max_keys: number;
  total_keys: number;
  is_flat_list: boolean;
  objects: S3Files[];
  page_map: { [key: number]: string } | Record<string, never>;
  is_next_page_available: boolean;
};

export type GetFlatFilesParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  max_keys: number;
  page: number;
  is_flat_list: boolean;
  prefix?: string;
  delimiter?: string;
  page_map: { [key: number]: string } | Record<string, never>;
};

export type UploadFileParams = {
  regionId: string | Provider<string> | undefined;
  projId: string;
  accessKey: string;
  accessSecret: string;
  endpoint: string | EndpointV2 | undefined;
  bucketName: string;
  file: File;
  folder: string;
};
export type UploadFileResponse = { message: string };

export type DeleteFileParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  fileName: string;
};

export type DeleteFiles = {
  key: string;
  is_folder: boolean;
};

export type DeleteFilesParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  objects?: DeleteFiles[];
};

export type DeleteFileAllVersionsParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  data: { object_keys: string[] };
};

export type FileVersions = {
  object_key: string;
  versions: {
    version_id: string;
    is_latest: boolean;
    last_modified: string;
    size: number;
    etag: string;
    storage_class: string;
  }[];
};

export type GetFileAllVersionsParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  fileName: string;
};

export type GetFileAllVersionsResponse = FileVersions;

export type CreateFolderParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  folderName: string;
};

export type GetFilePublicLinkParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  fileName: string;
};

export type DownloadFileParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  fileName: string;
};

export type DownloadFileResponse = string;

export type GetFilePublicLinkResponse = string;

export type GetFileSignedDownloadLinkParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  fileName: string;
  duration: number;
};

export type GetFileSignedDownloadLinkResponse = string;

export type ChangeBucketVisibilityParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  visibility: string;
};

// expiration date
export type BucketLifecycleRule = {
  id: string;
  status: string;
  days: number;
};

export type GetBucketLifecycleRuleParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
};
export type GetBucketLifecycleRuleResponse = BucketLifecycleRule;

export type SetBucketLifecycleRuleParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  data: {
    expiration_days: number;
  };
};
export type SetBucketLifecycleRuleResponse = {
  expiration_days: number;
  message: string;
};

export type DeleteBucketLifecycleRuleParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
};
export type DeleteBucketLifecycleRuleResponse = {
  message: string;
};

export type CheckCorsPolicyParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
};

export type CorsPolicy = { isAccessToBucketAllowed: boolean };
export type CheckCorsPolicyResponse = CorsPolicy;

export type SetCorsPolicyParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
};

export type UpdateVersioningStatusParams = {
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
  versioningStatus: string; // "enabled" | "suspended"
};

/*
  Bucket policies.

  The rule sets cannot be predicted in advance: the API may return fewer keys,
  extra keys or differently shaped rules for every bucket. So only the keys we
  currently know about are described, all of them are optional, and every object
  stays open through an `unknown` index signature. The UI renders the raw JSON,
  it does not rely on any particular key being present.
*/

export type BucketPolicyStatement = {
  Sid?: string;
  Effect?: string;
  Principal?: unknown;
  Action?: string | string[];
  Resource?: string | string[];
  Condition?: Record<string, unknown>;
  [key: string]: unknown;
};

export type BucketPolicy = {
  Version?: string;
  Id?: string;
  Statement?: BucketPolicyStatement[];
  [key: string]: unknown;
};

export type BucketLifecyclePolicyRule = {
  ID?: string;
  Status?: string;
  Filter?: Record<string, unknown>;
  Transitions?: Record<string, unknown>[];
  Expiration?: Record<string, unknown>;
  NoncurrentVersionExpiration?: Record<string, unknown>;
  AbortIncompleteMultipartUpload?: Record<string, unknown>;
  [key: string]: unknown;
};

export type BucketLifecyclePolicy = {
  Rules?: BucketLifecyclePolicyRule[];
  [key: string]: unknown;
};

export type BucketCorsRule = {
  ID?: string;
  AllowedOrigins?: string[];
  AllowedMethods?: string[];
  AllowedHeaders?: string[];
  ExposedHeaders?: string[];
  MaxAgeSeconds?: number;
  [key: string]: unknown;
};

export type BucketCorsPolicy = {
  CORSRules?: BucketCorsRule[];
  [key: string]: unknown;
};

export type BucketAclGrant = {
  Grantee?: {
    DisplayName?: string;
    ID?: string;
    Type?: string;
    URI?: string;
    [key: string]: unknown;
  };
  Permission?: string;
  [key: string]: unknown;
};

export type BucketAcl = {
  Owner?: {
    DisplayName?: string;
    ID?: string;
    [key: string]: unknown;
  };
  Grants?: BucketAclGrant[];
  [key: string]: unknown;
};

/*
  Every policy is fetched by its own request. They all take the same params as
  the other per-bucket requests, and each of them may come back empty when the
  bucket has no such policy.
*/
export type GetBucketPolicyParams = {
  pollingId?: string;
  regionId: string;
  projId: string;
  credsId: string;
  bucketName: string;
};
export type GetBucketPolicyResponse = BucketPolicy;

export type GetBucketLifecyclePolicyParams = GetBucketPolicyParams;
export type GetBucketLifecyclePolicyResponse = BucketLifecyclePolicy;

export type GetBucketCorsPolicyParams = GetBucketPolicyParams;
export type GetBucketCorsPolicyResponse = BucketCorsPolicy;

export type GetBucketAclParams = GetBucketPolicyParams;
export type GetBucketAclResponse = BucketAcl;

export enum BUCKET_POLICY_TYPES {
  POLICY = "policy",
  LIFECYCLE = "lifecycle_policy",
  CORS = "cors_policy",
  ACL = "acl"
}

export const BUCKET_POLICY_TYPES_LABELS: {
  [key in BUCKET_POLICY_TYPES]: string;
} = {
  [BUCKET_POLICY_TYPES.POLICY]: "Bucket policy",
  [BUCKET_POLICY_TYPES.LIFECYCLE]: "Lifecycle policy",
  [BUCKET_POLICY_TYPES.CORS]: "CORS policy",
  [BUCKET_POLICY_TYPES.ACL]: "ACL"
};

/*
  UI-ready representation of a single policy. There is always one entry per
  policy type: the ones the bucket has carry the JSON, the rest are listed as
  not configured, so they can later be used to create or assign a policy.
*/
export type BucketPolicyDocument = {
  type: BUCKET_POLICY_TYPES;
  label: string;
  isConfigured: boolean;
  json: string;
};
